import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, Trash2 } from 'lucide-react';
import { Button, ConfirmDialog, Tabs } from '@/design-system';
import { useAuth } from '@/core/auth';
import {
  managerConnectorService,
  type ManagerConnectorGateway,
} from '@/features/connector-gateways/api/connector-gateways.service';
import { GatewayTopFailures } from '@/features/connector-gateways/GatewayTopFailures';
import { PauseUpdatesControl } from '@/features/connector-gateways/PauseUpdatesControl';
import { VerboseLogControl } from '@/features/connector-gateways/VerboseLogControl';
import { RepairPairingDialog } from '@/features/connector-gateways/RepairPairingDialog';

interface Props {
  companyId: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  pairing: 'border-amber-200 bg-amber-50 text-amber-700',
  bootstrapping: 'border-blue-200 bg-blue-50 text-blue-700',
  paused: 'border-gray-200 bg-gray-50 text-gray-700',
  offline: 'border-orange-200 bg-orange-50 text-orange-700',
  error: 'border-red-200 bg-red-50 text-red-700',
};

/**
 * Issue #74: 1 conector por tenant — exibição single-state.
 *
 * Shows the tenant's single gateway. Disconnect preserves all related data;
 * re-pairing renews its credentials in place. SUPERADMIN/SUPPORT only.
 */
export function CompanyConnectorSection({ companyId }: Props) {
  const queryClient = useQueryClient();
  const { user: staffUser } = useAuth();
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState('gateway');
  const [repairOpen, setRepairOpen] = useState(false);

  const query = useQuery({
    queryKey: ['manager-connector-gateways', { companyId }],
    queryFn: () => managerConnectorService.listGateways({ companyId }),
    enabled: !!companyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (gatewayId: string) => managerConnectorService.deleteGateway(gatewayId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manager-connector-gateways'] });
      void queryClient.invalidateQueries({ queryKey: ['manager-connector-overview'] });
      setConfirmTargetId(null);
      setErrorMessage(null);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : 'Falha ao desconectar conector. Tente novamente.';
      setErrorMessage(msg);
    },
  });

  const gateway = query.data?.[0];
  const canManage = staffUser?.role === 'SUPERADMIN' || staffUser?.role === 'SUPPORT';

  // Fecha o dialog se o gateway sumir (deletado em outra aba, refetch concorrente).
  useEffect(() => {
    if (confirmTargetId && !query.data?.some((g) => g.id === confirmTargetId)) {
      setConfirmTargetId(null);
    }
  }, [query.data, confirmTargetId]);

  return (
    <div className="space-y-4">

      {query.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : query.isError ? (
        <p className="text-sm text-red-600">
          Falha ao carregar conector. Recarregue a página.
        </p>
      ) : !gateway ? (
        <EmptyState />
      ) : (
        <Tabs label="Detalhes do Orya Connector" value={tab} onChange={setTab} tabs={[
          { value: 'gateway', label: 'Gateway', content: <ActiveState gateway={gateway} canManage={canManage} onDisconnect={() => setConfirmTargetId(gateway.id)} onRepair={() => setRepairOpen(true)} /> },
          { value: 'updates', label: 'Atualizações', content: <PauseUpdatesControl gatewayId={gateway.id} pausedUntil={gateway.updatesPausedUntil} pausedReason={gateway.updatesPausedReason} canManage={canManage} /> },
          { value: 'diagnostics', label: 'Diagnóstico', content: <div className="space-y-4"><GatewayTopFailures gatewayId={gateway.id} /><VerboseLogControl gatewayId={gateway.id} canManage={canManage} /></div> },
        ]} />
      )}

      <ConfirmDialog
        isOpen={confirmTargetId !== null}
        onClose={() => {
          setConfirmTargetId(null);
          setErrorMessage(null);
        }}
        onConfirm={() => confirmTargetId && deleteMutation.mutate(confirmTargetId)}
        title="Desconectar conector?"
        message={
          gateway
            ? `A conexão será desativada e a credencial revogada. Clientes, vínculos, orçamentos e histórico serão preservados. Para conectar novamente, use Refazer pareamento.${errorMessage ? `\n\nErro: ${errorMessage}` : ''}`
            : ''
        }
        confirmText="Desconectar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
      {repairOpen && gateway && <RepairPairingDialog gatewayId={gateway.id} onClose={() => setRepairOpen(false)} />}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-3">
      <div className="text-sm">
        <p className="font-medium text-gray-700">Sem conector pareado</p>
        <p className="text-xs text-gray-500">
          O tenant pode parear um conector via Integrações → FarmaFácil no app dele.
        </p>
      </div>
      <Link
        to="/connector-gateways"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:underline"
      >
        Ver parque <ExternalLink size={12} aria-hidden="true" />
      </Link>
    </div>
  );
}

function ActiveState({
  gateway,
  canManage,
  onDisconnect,
  onRepair,
}: {
  gateway: ManagerConnectorGateway;
  canManage: boolean;
  onDisconnect: () => void;
  onRepair: () => void;
}) {
  const status = gateway.effectiveStatus || gateway.status;
  const statusClass = STATUS_COLORS[status] ?? 'border-gray-200 bg-gray-50 text-gray-700';
  const lastSeen = gateway.lastSeenAt
    ? new Date(gateway.lastSeenAt).toLocaleString('pt-BR')
    : '—';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">{gateway.name}</span>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass}`}
            >
              {status}
            </span>
            <span className="font-mono text-xs text-gray-500">{gateway.adapterId}</span>
          </div>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2 xl:grid-cols-3">
            <Field label="Adapter version" value={gateway.adapterVersion ?? '—'} />
            <Field
              label="Manifest"
              value={
                gateway.manifestVersion ?? (
                  <span className="text-gray-400">legacy</span>
                )
              }
            />
            <Field label="Canal" value={gateway.releaseChannel} />
            <Field
              label="Versão alvo"
              value={gateway.targetVersion ?? <span className="text-gray-400">auto</span>}
            />
            <Field label="Último ping" value={lastSeen} />
            <Field label="Outbox" value={gateway.outboxDepth ?? '—'} />
            <Field label="DLQ pendente" value={gateway.dlqCount} />
          </dl>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Link
            to="/connector-gateways"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:underline"
          >
            Abrir no parque <ExternalLink size={12} aria-hidden="true" />
          </Link>
          {canManage && (
            <Button size="sm" variant="outline" onClick={onRepair}>Refazer pareamento</Button>
          )}
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={onDisconnect}
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <Trash2 size={14} className="mr-1.5" aria-hidden="true" />
              Desconectar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value}</dd>
    </div>
  );
}
